// The few things we ask only when the trip needs them (backend core/questions), one at a time.
// Each question shows what we ask, why it matters and what we recommend; skipping is always
// there, small, and says what we'll do instead. The answers go back keyed by question.
// Look is functional for now: the final design comes with the new onboarding.
import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Check } from "lucide-react";

export const SKIP = "__skip__";

const OptionButton = ({ option, selected, onClick, multi }) => (
  <button type="button" onClick={onClick} data-testid={`q-option-${option.value}`}
    style={{
      display: "flex", alignItems: "flex-start", gap: 12, width: "100%", textAlign: "left", cursor: "pointer",
      padding: "14px 16px", borderRadius: 4,
      background: selected ? "var(--zen-text)" : "var(--zen-s1)",
      color: selected ? "var(--zen-bg)" : "var(--zen-text)",
      border: `1px solid ${selected ? "var(--zen-text)" : option.recommended ? "var(--zen-accent)" : "var(--zen-border)"}`,
    }}>
    {multi && (
      <span aria-hidden="true" style={{
        width: 18, height: 18, flexShrink: 0, marginTop: 1, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: `1.5px solid ${selected ? "var(--zen-bg)" : "var(--zen-dim)"}`,
      }}>{selected && <Check size={13} />}</span>
    )}
    <span style={{ flex: 1 }}>
      <span style={{ display: "block", fontWeight: 600, fontSize: 15 }}>
        {option.label}
        {option.recommended && (
          <span className="font-mono-label" style={{ marginLeft: 8, fontSize: 9, letterSpacing: "0.16em", color: selected ? "var(--zen-bg)" : "var(--zen-accent)" }}>
            OUR PICK
          </span>
        )}
      </span>
      {option.hint && <span style={{ display: "block", fontSize: 12.5, marginTop: 2, opacity: 0.7 }}>{option.hint}</span>}
    </span>
  </button>
);

const QuestionFlow = ({ questions, onDone }) => {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [picked, setPicked] = useState([]);
  const [typed, setTyped] = useState("");
  const q = questions[index];

  const go = (value) => {
    const next = { ...answers, [q.key]: value };
    setAnswers(next);
    setPicked([]);
    setTyped("");
    if (index + 1 >= questions.length) onDone(next);
    else setIndex(index + 1);
  };
  const back = () => {
    if (index === 0) return;
    const prev = questions[index - 1];
    const was = answers[prev.key];
    setPicked(Array.isArray(was) ? was : []);
    setTyped("");
    setIndex(index - 1);
  };
  if (!q) return null;
  const toggle = (value) => setPicked(p => (p.includes(value) ? p.filter(v => v !== value) : [...p, value]));

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--zen-bg)" }} data-testid="question-flow">
      <motion.div key={q.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="w-full px-5" style={{ maxWidth: 520, paddingTop: 32, paddingBottom: 32 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 22 }}>
          <button type="button" onClick={back} disabled={index === 0} aria-label="Previous question"
            style={{ visibility: index === 0 ? "hidden" : "visible", color: "var(--zen-sub)", background: "none", border: 0, cursor: "pointer", padding: 0 }}>
            <ChevronLeft size={20} />
          </button>
          <span className="font-mono-label" style={{ fontSize: 10, letterSpacing: "0.2em", color: "var(--zen-dim)" }}>
            BEFORE WE CHOOSE · {index + 1} OF {questions.length}
          </span>
          <span style={{ width: 20 }} />
        </div>

        <h2 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 700, fontSize: 24, lineHeight: 1.25, color: "var(--zen-text)" }}>
          {q.ask}
        </h2>
        <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.55, color: "var(--zen-sub)" }}>{q.why}</p>
        {q.advice && (
          <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: "var(--zen-text)", borderLeft: "2px solid var(--zen-accent)", paddingLeft: 10 }}>
            {q.advice}
          </p>
        )}

        <div className="space-y-2" style={{ marginTop: 20 }}>
          {q.options.map(o => (
            <OptionButton key={o.value} option={o} multi={q.multi}
              selected={q.multi ? picked.includes(o.value) : false}
              onClick={() => (q.multi ? toggle(o.value) : go(o.value))} />
          ))}
        </div>

        {q.free_text && (
          <form onSubmit={e => { e.preventDefault(); if (typed.trim()) go(typed.trim()); }} className="flex gap-2" style={{ marginTop: 10 }}>
            <input type="text" value={typed} onChange={e => setTyped(e.target.value)} placeholder={q.free_text}
              style={{ flex: 1, padding: "12px 14px", borderRadius: 4, border: "1px solid var(--zen-border)", background: "var(--zen-bg)", fontSize: 14 }} />
            <button type="submit" className="zen-btn-primary" disabled={!typed.trim()} style={{ padding: "0 16px" }}>Use this</button>
          </form>
        )}

        {q.multi && (
          <button type="button" className="zen-btn-primary w-full" style={{ marginTop: 16, padding: "13px 0" }}
            onClick={() => go(picked)} data-testid="q-continue">
            {picked.length ? "Continue" : "None of these — continue"}
          </button>
        )}

        <div style={{ marginTop: 26, textAlign: "center" }}>
          <button type="button" onClick={() => go(SKIP)} data-testid="q-skip"
            style={{ background: "none", border: 0, cursor: "pointer", fontSize: 12.5, color: "var(--zen-dim)", textDecoration: "underline" }}>
            Skip
          </button>
          <p style={{ fontSize: 11.5, color: "var(--zen-dim)", marginTop: 4 }}>{q.if_skipped}</p>
        </div>
      </motion.div>
    </div>
  );
};

export default QuestionFlow;
